import { AlertTriangle, Briefcase, CheckCircle2 } from 'lucide-react';
import { isFailClosed } from '@/lib/agent-platform';
import { cn } from '@/lib/utils';
import { FailClosedNotice } from './fail-closed-notice';

interface BusinessRiskMatch {
  evidenceId: string;
  source: 'SIC_CODE' | 'OCCUPATION' | 'SOURCE_OF_FUNDS' | 'EMPLOYER';
  category:
    | 'FINANCIAL_SERVICES'
    | 'LEGAL_ACCOUNTANCY'
    | 'REAL_ESTATE'
    | 'CASH_INTENSIVE'
    | 'IMPORT_EXPORT'
    | 'CRYPTO_FINTECH'
    | 'CHARITIES'
    | 'HIGH_VALUE_DEALERS';
  matchedText: string;
  riskLevel: 'HIGH' | 'MEDIUM' | 'LOW' | 'NONE';
  confidence: number;
  reasoning: string;
}

interface BusinessRiskSectionProps {
  matches?: BusinessRiskMatch[];
  hasHighRiskBusiness?: boolean;
  categories?: string[];
}

const SOURCE_LABELS: Record<BusinessRiskMatch['source'], string> = {
  SIC_CODE: 'SIC Code',
  OCCUPATION: 'Occupation',
  SOURCE_OF_FUNDS: 'Source of Funds',
  EMPLOYER: 'Employer',
};

const CATEGORY_LABELS: Record<BusinessRiskMatch['category'], string> = {
  FINANCIAL_SERVICES: 'Financial Services',
  LEGAL_ACCOUNTANCY: 'Legal & Accountancy',
  REAL_ESTATE: 'Real Estate',
  CASH_INTENSIVE: 'Cash Intensive Business',
  IMPORT_EXPORT: 'Import/Export',
  CRYPTO_FINTECH: 'Crypto & Fintech',
  CHARITIES: 'Charities & NPOs',
  HIGH_VALUE_DEALERS: 'High Value Dealers',
};

const RISK_LEVEL_STYLES: Record<BusinessRiskMatch['riskLevel'], { bg: string; text: string }> = {
  HIGH: { bg: 'bg-red-100', text: 'text-red-800' },
  MEDIUM: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
  LOW: { bg: 'bg-green-100', text: 'text-green-800' },
  NONE: { bg: 'bg-gray-100', text: 'text-gray-800' },
};

export function BusinessRiskSection({
  matches,
  hasHighRiskBusiness,
  categories,
}: BusinessRiskSectionProps) {
  const introCopy = (
    <p className="mt-3 text-sm leading-relaxed text-gray-600">
      This check reviews the subject's declared occupation and source of income and flags
      associations with FATF-recognised high money-laundering-risk business categories — for example
      MSBs, TCSPs, cash-intensive businesses, and crypto/fintech. It focuses on owners and directors
      rather than employees of regulated firms. It runs automatically on every screening.
    </p>
  );

  const automaticBadge = (
    <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
      Automatic
    </span>
  );

  // If no matches and no high-risk business detected, show a clean result
  if (!matches || matches.length === 0) {
    if (!hasHighRiskBusiness) {
      return (
        <section className="mb-8 rounded-lg border bg-white p-6 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-green-100 p-2">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold text-[#051923]">Business Profile Check</h2>
                {automaticBadge}
              </div>
              <p className="text-sm text-gray-600">
                No high-risk business categories identified in the subject's declared profile.
              </p>
              {introCopy}
            </div>
          </div>
        </section>
      );
    }
    return null;
  }

  return (
    <section className="mb-8 rounded-lg border bg-white p-6 shadow-sm">
      <div className="mb-4 flex items-start gap-3">
        <div
          className={cn('rounded-full p-2', hasHighRiskBusiness ? 'bg-amber-100' : 'bg-green-100')}
        >
          {hasHighRiskBusiness ? (
            <AlertTriangle className="h-5 w-5 text-amber-600" />
          ) : (
            <Briefcase className="h-5 w-5 text-green-600" />
          )}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-[#051923]">Business Profile Check</h2>
            {automaticBadge}
          </div>
          {introCopy}
        </div>
      </div>

      {categories && categories.length > 0 && (
        <div className="mb-4">
          <p className="mb-2 text-sm text-gray-600">High-risk categories identified:</p>
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <span
                key={category}
                className="rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-800"
              >
                {CATEGORY_LABELS[category as BusinessRiskMatch['category']] || category}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-4">
        {matches.map((match, index) => {
          const riskStyles = RISK_LEVEL_STYLES[match.riskLevel];

          return (
            <div
              key={match.evidenceId || `match-${index}`}
              className="rounded-lg border border-gray-200 p-4"
            >
              <div className="mb-2 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">
                    {SOURCE_LABELS[match.source]}
                  </span>
                  <span className="text-gray-400">•</span>
                  <span className="text-sm text-gray-600">
                    {CATEGORY_LABELS[match.category] || match.category}
                  </span>
                </div>
                <span
                  className={cn(
                    'rounded-full px-2 py-0.5 text-xs font-medium',
                    riskStyles.bg,
                    riskStyles.text,
                  )}
                >
                  {match.riskLevel}
                </span>
              </div>

              <div className="mb-2">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Matched text:</span> "{match.matchedText}"
                </p>
              </div>

              {match.reasoning &&
                (isFailClosed(match.reasoning) ? (
                  <FailClosedNotice fieldLabel="Reasoning" variant="inline" />
                ) : (
                  <div className="rounded-md bg-gray-50 p-3">
                    <p className="text-sm leading-relaxed text-gray-700">{match.reasoning}</p>
                  </div>
                ))}
            </div>
          );
        })}
      </div>
    </section>
  );
}
